//@flow
import {useCallback, useEffect, useReducer} from 'react';

import type {AdminAddress} from '../../../entities/logistic';
import {fetchAdmin1, fetchAdmin2, fetchAdmin3} from '../../../utils/location';

import {adminResponseToAdminAddress} from '../utils/admin-response-to-admin-address';

type AddressState = {
  admin1List: AdminAddress[],
  admin2List: AdminAddress[],
  admin3List: AdminAddress[],
  selectedAdmin1?: AdminAddress,
  selectedAdmin2?: AdminAddress,
  selectedAdmin3?: AdminAddress,
  loading: boolean,
  countryCode: string,
};

type AdminLevel = 'admin1' | 'admin2' | 'admin3';

const ActionKind = {
  SET_ADMIN_LIST: 'SET_ADMIN_LIST',
  SET_SELECTED_ADMIN: 'SET_SELECTED_ADMIN',
  SET_LOADING: 'SET_LOADING',
  SET_ADMINS: 'SET_ADMINS',
  SET_COUNTRY_CODE: 'SET_COUNTRY_CODE',
  RESET_STATE: 'RESET_STATE',
};

type Action =
  | {
      type: 'SET_ADMIN_LIST',
      payload: {level: AdminLevel, list: AdminAddress[]},
    }
  | {
      type: 'SET_SELECTED_ADMIN',
      payload: {level: AdminLevel, adminIdOrName?: string},
    }
  | {type: 'SET_LOADING', payload: boolean}
  | {type: 'SET_ADMINS', payload: any} // -> Partial<AddressState>
  | {type: 'SET_COUNTRY_CODE', payload: string}
  | {type: 'RESET_STATE'};

const initialState: AddressState = {
  admin1List: [],
  admin2List: [],
  admin3List: [],
  loading: false,
  countryCode: '',
};

function reducer(state: AddressState, action: Action): AddressState {
  switch (action.type) {
    case ActionKind.SET_ADMIN_LIST: {
      if (action.payload.level === 'admin1') {
        return {
          ...state,
          admin1List: action.payload.list,
          selectedAdmin1: undefined,
          selectedAdmin2: undefined,
          selectedAdmin3: undefined,
          admin2List: [],
          admin3List: [],
        };
      }
      if (action.payload.level === 'admin2') {
        return {
          ...state,
          admin2List: action.payload.list,
          selectedAdmin2: undefined,
          selectedAdmin3: undefined,
          admin3List: [],
        };
      }
      if (action.payload.level === 'admin3') {
        return {
          ...state,
          admin3List: action.payload.list,
          selectedAdmin3: undefined,
        };
      }
      return state;
    }

    case ActionKind.SET_SELECTED_ADMIN: {
      if (action.payload.level === 'admin1') {
        const adminAddress = state.admin1List.find(
          admin =>
            admin.adminName === action.payload.adminIdOrName ||
            admin.adminId.toString() === action.payload.adminIdOrName
        );

        return {
          ...state,
          selectedAdmin1: adminAddress,
          selectedAdmin2: undefined,
          selectedAdmin3: undefined,
          admin2List: [],
          admin3List: [],
        };
      }
      if (action.payload.level === 'admin2') {
        const adminAddress = state.admin2List.find(
          admin =>
            admin.adminName === action.payload.adminIdOrName ||
            admin.adminId.toString() === action.payload.adminIdOrName
        );
        return {
          ...state,
          selectedAdmin2: adminAddress,
          selectedAdmin3: undefined,
          admin3List: [],
        };
      }
      if (action.payload.level === 'admin3') {
        const adminAddress = state.admin3List.find(
          admin =>
            admin.adminName === action.payload.adminIdOrName ||
            admin.adminId.toString() === action.payload.adminIdOrName
        );
        return {...state, selectedAdmin3: adminAddress};
      }
      return state;
    }

    case ActionKind.SET_LOADING:
      return {...state, loading: action.payload};

    case ActionKind.SET_ADMINS:
      return {...state, ...action.payload};

    case ActionKind.SET_COUNTRY_CODE:
      return {...state, countryCode: action.payload};

    case ActionKind.RESET_STATE:
      return initialState;

    default:
      return state;
  }
}

export function useAddressSelector(initialCountryCode: string) {
  const [state, dispatch] = useReducer(reducer, {
    ...initialState,
    countryCode: initialCountryCode,
  });

  const fetchAdmin1List = useCallback(() => {
    if (!state.countryCode) return;
    dispatch({type: ActionKind.SET_LOADING, payload: true});
    fetchAdmin1(state.countryCode)
      .then(admin1Response => {
        const admin1List: AdminAddress[] = admin1Response.map(
          adminResponseToAdminAddress
        );
        dispatch({
          type: ActionKind.SET_ADMIN_LIST,
          payload: {level: 'admin1', list: admin1List},
        });
      })
      .catch(console.error)
      .finally(() => dispatch({type: ActionKind.SET_LOADING, payload: false}));
  }, [state.countryCode]);

  const fetchAdmin2List = useCallback(() => {
    if (!state.countryCode || !state.selectedAdmin1) return;
    dispatch({type: ActionKind.SET_LOADING, payload: true});
    //$FlowFixMe
    fetchAdmin2(state.countryCode, state.selectedAdmin1.adminId.toString())
      .then(admin2Response => {
        const admin2List: AdminAddress[] = admin2Response.map(
          adminResponseToAdminAddress
        );
        dispatch({
          type: ActionKind.SET_ADMIN_LIST,
          payload: {level: 'admin2', list: admin2List},
        });
      })
      .catch(console.error)
      .finally(() => dispatch({type: ActionKind.SET_LOADING, payload: false}));
  }, [state.countryCode, state.selectedAdmin1]);

  const fetchAdmin3List = useCallback(() => {
    if (!state.countryCode || !state.selectedAdmin1 || !state.selectedAdmin2)
      return;
    dispatch({type: ActionKind.SET_LOADING, payload: true});
    fetchAdmin3(
      state.countryCode,
      //$FlowFixMe
      state.selectedAdmin1.adminId.toString(),
      //$FlowFixMe
      state.selectedAdmin2.adminId.toString()
    )
      .then(admin3Response => {
        const admin3List: AdminAddress[] = admin3Response.map(
          adminResponseToAdminAddress
        );
        dispatch({
          type: ActionKind.SET_ADMIN_LIST,
          payload: {level: 'admin3', list: admin3List},
        });
      })
      .catch(console.error)
      .finally(() => dispatch({type: ActionKind.SET_LOADING, payload: false}));
  }, [state.countryCode, state.selectedAdmin1, state.selectedAdmin2]);

  useEffect(() => {
    dispatch({type: ActionKind.RESET_STATE});
    dispatch({type: ActionKind.SET_COUNTRY_CODE, payload: initialCountryCode});
    fetchAdmin1List();
  }, [initialCountryCode, fetchAdmin1List]);

  useEffect(fetchAdmin2List, [fetchAdmin2List]);
  useEffect(fetchAdmin3List, [fetchAdmin3List]);

  const selectAdmin1 = useCallback((adminIdOrName: string) => {
    dispatch({
      type: ActionKind.SET_SELECTED_ADMIN,
      payload: {level: 'admin1', adminIdOrName},
    });
  }, []);

  const selectAdmin2 = useCallback((adminIdOrName: string) => {
    dispatch({
      type: ActionKind.SET_SELECTED_ADMIN,
      payload: {level: 'admin2', adminIdOrName},
    });
  }, []);

  const selectAdmin3 = useCallback((adminIdOrName: string) => {
    dispatch({
      type: ActionKind.SET_SELECTED_ADMIN,
      payload: {level: 'admin3', adminIdOrName},
    });
  }, []);

  const setAdmins = useCallback(
    async (
      admin1IdOrName: string,
      admin2IdOrName?: string,
      admin3IdOrName?: string
    ) => {
      if (!state.countryCode) return;

      const admin1 = state.admin1List.find(
        admin =>
          admin.adminName === admin1IdOrName ||
          admin.adminId.toString() === admin1IdOrName
      );
      if (!admin1) return;

      const admin2Response = await fetchAdmin2(
        state.countryCode,
        admin1.adminId.toString()
      );
      const admin2List: AdminAddress[] = admin2Response.map(
        adminResponseToAdminAddress
      );

      const admin2 = admin2List.find(
        admin =>
          admin.adminName === admin2IdOrName ||
          admin.adminId.toString() === admin2IdOrName
      );

      if (!admin2) {
        dispatch({
          type: ActionKind.SET_ADMINS,
          payload: {
            selectedAdmin1: admin1,
            admin2List,
            admin3List: [],
            selectedAdmin2: undefined,
            selectedAdmin3: undefined,
          },
        });
        return;
      }

      const admin3Response = await fetchAdmin3(
        state.countryCode,
        admin1.adminId.toString(),
        admin2.adminId.toString()
      );
      const admin3List: AdminAddress[] = admin3Response.map(
        adminResponseToAdminAddress
      );

      const admin3 = admin3List.find(
        admin =>
          admin.adminName === admin3IdOrName ||
          admin.adminId.toString() === admin3IdOrName
      );

      dispatch({
        type: ActionKind.SET_ADMINS,
        payload: {
          selectedAdmin1: admin1,
          selectedAdmin2: admin2,
          selectedAdmin3: admin3,
          admin2List,
          admin3List,
        },
      });
    },
    [state.countryCode, state.admin1List]
  );

  return {
    ...state,
    selectAdmin1,
    selectAdmin2,
    selectAdmin3,
    setAdmins,
  };
}
